import { formatReorderNumber, reorderParamsFrom, type ReorderParams } from '@/lib/reorder'
import { ReorderCalcInputs, ReorderCalcResults } from './reorder-calculator/ReorderCalcView'

/** The settings the calculator lets you type in. Stock is never typed: it is the material's live stock. */
export type ReorderCalcField = 'dailyConsumption' | 'orderLeadTimeDays' | 'releaseTimeDays' | 'safetyStockDays'

const INPUTS: { key: ReorderCalcField; label: string; unit: string }[] = [
  { key: 'dailyConsumption', label: 'Daily consumption', unit: 'units' },
  { key: 'orderLeadTimeDays', label: 'Order lead time', unit: 'days' },
  { key: 'releaseTimeDays', label: 'Release time', unit: 'days' },
  { key: 'safetyStockDays', label: 'Safety stock', unit: 'days' },
]

/** The material's stock as the inventory holds it now. */
export interface LiveStock {
  onHand: number
  available: number
  onOrder: number
}

interface ReorderCalculatorPanelProps {
  /** Reads a re-order parameter as stored or as typed — strings are fine. */
  get: (key: keyof ReorderParams) => unknown
  stock: LiveStock
  /**
   * Present: the four settings render and edit through this. Absent: results
   * only, for a screen whose own fields already hold the inputs (the
   * inventory_unit editor), so they are not typed in twice.
   */
  onChange?: (key: ReorderCalcField, raw: string) => void
}

/**
 * The material-bound re-order calculator (z8nrz7f7dz), in the Re-order dialog
 * and the inventory_unit editor. The settings are editable; the stock is the
 * material's live on hand / available / on order, read-only, so the decision
 * is always made against what is really on the shelf. Same pieces as the
 * standalone calculator, so both read alike; every figure comes from
 * src/lib/reorder.ts.
 */
export function ReorderCalculatorPanel({ get, stock, onChange }: ReorderCalculatorPanelProps) {
  const params = reorderParamsFrom(get)
  const raw = (key: ReorderCalcField): string => {
    const v = get(key)
    return v == null ? '' : String(v)
  }

  return (
    <div className='space-y-4 rounded-xl border border-slate-200 bg-slate-50/50 p-4' data-testid='reorder-calculator'>
      <h3 className='text-sm font-semibold text-slate-900'>Re-order calculator</h3>
      {onChange && (
        <ReorderCalcInputs
          fields={INPUTS.map((f) => ({ ...f, value: raw(f.key) }))}
          onChange={(key, v) => onChange(key as ReorderCalcField, v)}
        />
      )}
      <dl className='grid grid-cols-3 divide-x divide-slate-200 rounded-lg border border-slate-200 bg-white' data-testid='reorder-live-stock'>
        {(
          [
            ['On hand', stock.onHand],
            ['Available', stock.available],
            ['On order', stock.onOrder],
          ] as const
        ).map(([label, n]) => (
          <div key={label} className='px-3 py-2'>
            <dt className='text-[11px] font-medium uppercase tracking-wide text-slate-500'>{label}</dt>
            <dd className='mt-0.5 text-sm font-semibold tabular-nums text-slate-900'>{formatReorderNumber(n)}</dd>
          </div>
        ))}
      </dl>
      <p className='-mt-2 text-[11px] text-slate-500'>Current stock from inventory, live.</p>
      <ReorderCalcResults params={params} currentStock={stock.available} onHand={stock.onHand} onOrder={stock.onOrder} />
    </div>
  )
}
